<template>
  <Teleport to="body">
    <Transition name="modal">
      <div 
        v-if="isOpen" 
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        @click.self="handleClose"
      >
        <div 
          class="bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden w-full"
          :class="{
            'max-w-sm': size === 'sm',
            'max-w-md': size === 'md',
            'max-w-2xl': size === 'lg',
            'max-w-4xl': size === 'xl',
          }"
        >
          <!-- Header -->
          <div 
            v-if="title || showClose" 
            class="flex items-center justify-between px-6 py-4 border-b border-neutral-200"
          >
            <h3 
              v-if="title" 
              class="flex items-center gap-3 text-xl font-display font-semibold text-neutral-900"
            >
              <Icon 
                v-if="icon" 
                :name="icon" 
                size="md" 
                class="text-primary-600"
              />
              {{ title }}
            </h3>
            <button 
              v-if="showClose" 
              @click="handleClose" 
              class="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <Icon name="x" size="md" />
            </button>
          </div>

          <!-- Body -->
          <div class="px-6 py-4 overflow-y-auto flex-1">
            <div 
              v-if="message" 
              class="text-neutral-700 mb-4 leading-relaxed"
            >
              {{ message }}
            </div>
            <slot />
          </div>

          <!-- Footer -->
          <div 
            v-if="showFooter" 
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200"
          >
            <button 
              v-if="showCancel"
              @click="handleCancel" 
              class="btn btn-ghost"
            >
              {{ cancelText }}
            </button>
            <button 
              @click="handleConfirm" 
              class="btn"
              :class="confirmButtonClass"
              :disabled="loading"
            >
              <span 
                v-if="loading" 
                class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"
              ></span>
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Icon } from '@/components/icons'

interface Props {
  isOpen: boolean
  title?: string
  message?: string
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showClose?: boolean
  showFooter?: boolean
  showCancel?: boolean
  confirmText?: string
  cancelText?: string
  loading?: boolean
  closeOnOverlay?: boolean
  icon?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'md',
  showClose: true,
  showFooter: true,
  showCancel: true,
  confirmText: 'OK',
  cancelText: 'Cancel',
  loading: false,
  closeOnOverlay: true,
  icon: undefined,
})

const emit = defineEmits<{
  close: []
  confirm: []
  cancel: []
}>()

const confirmButtonClass = computed(() => {
  const variantMap = {
    default: 'btn-primary',
    success: 'btn-primary',
    error: 'btn-primary',
    warning: 'btn-accent',
    info: 'btn-primary',
  }
  return variantMap[props.variant]
})

function handleClose() {
  if (props.closeOnOverlay) {
    emit('close')
  }
}

function handleConfirm() {
  emit('confirm')
}

function handleCancel() {
  emit('cancel')
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>
